import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  Upload, 
  Bot, 
  Send, 
  FileCheck, 
  Sparkles, 
  Cpu 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format } from 'date-fns';

const TenderDetail = () => {
  const { id } = useParams();
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Analysis State
  const [query, setQuery] = useState('');
  const [analysis, setAnalysis] = useState('');
  const [analyzing, setAnalyzing] = useState(false);

  // Proposal State
  const [instructions, setInstructions] = useState('');
  const [proposal, setProposal] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchTender();
  }, [id]);

  const fetchTender = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tenders/${id}`);
      setTender(res.data);
    } catch (err) {
      toast.error("Failed to load tender details");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/tenders/${id}/upload`, formData);
      toast.success("Document uploaded successfully");
      fetchTender(); // Refresh to show new doc
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!query.trim()) return;
    setAnalyzing(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/tenders/analyze`, {
        tender_id: id,
        query: query
      });
      setAnalysis(res.data.analysis);
    } catch (err) {
      toast.error("Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerateProposal = async () => {
    if (!instructions.trim()) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/tenders/proposal`, {
        tender_id: id,
        instructions: instructions
      });
      setProposal(res.data.proposal);
    } catch (err) {
      toast.error("Proposal generation failed");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="p-8 text-center">Loading mission data...</div>;
  if (!tender) return <div className="p-8 text-center text-red-500">Tender not found</div>;

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-border pb-6">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold font-manrope tracking-tight text-foreground">{tender.title}</h1>
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">{tender.status}</Badge>
          </div>
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <span className="text-foreground font-medium">{tender.client_name}</span> • 
            <span className="font-mono">Deadline: {format(new Date(tender.deadline), 'PPP')}</span>
          </p>
        </div>
        <div className="flex gap-3">
           <Button variant="outline" className="gap-2 bg-card border-border text-muted-foreground hover:bg-accent">
             <FileCheck className="w-4 h-4" /> Mark Submitted
           </Button>
        </div>
      </div>

      {/* Main Workspace */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0">
        
        {/* Left Panel: Documents & Context */}
        <div className="lg:col-span-4 flex flex-col gap-4 min-h-0">
           <div className="bg-card border border-border rounded-lg p-4 flex-1 flex flex-col shadow-sm">
             <div className="flex items-center justify-between mb-4">
               <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Documents</h3>
               <div className="relative">
                 <input 
                   type="file" 
                   id="file-upload" 
                   className="hidden" 
                   onChange={handleFileUpload}
                   disabled={uploading}
                 />
                 <label htmlFor="file-upload" className="cursor-pointer">
                   <div className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-8 px-3 text-muted-foreground">
                     <Upload className="w-3 h-3 mr-1.5" /> {uploading ? 'Uploading...' : 'Upload PDF'}
                   </div>
                 </label>
               </div>
             </div>
             
             <ScrollArea className="flex-1 -mx-2 px-2">
               <div className="space-y-2">
                 {tender.documents.length === 0 && (
                   <p className="text-sm text-muted-foreground text-center py-8 italic">No documents uploaded yet.</p>
                 )}
                 {tender.documents.map((doc, i) => (
                   <div key={i} className="flex items-center gap-3 p-3 bg-accent/50 rounded-md border border-border hover:border-primary/30 transition-colors group cursor-pointer">
                     <div className="h-8 w-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                       <FileText className="w-4 h-4" />
                     </div>
                     <div className="flex-1 min-w-0">
                       <p className="text-sm font-medium text-foreground truncate">{doc}</p>
                       <p className="text-[10px] text-muted-foreground uppercase">Processed • 1.2MB</p>
                     </div>
                   </div>
                 ))}
               </div>
             </ScrollArea>
           </div>
        </div>

        {/* Right Panel: AI Workspace */}
        <div className="lg:col-span-8 flex flex-col min-h-0">
          <Tabs defaultValue="analyze" className="flex-1 flex flex-col">
            <TabsList className="bg-muted border border-border p-1 mb-4 w-fit">
              <TabsTrigger value="analyze" className="gap-2 data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm"><Bot className="w-4 h-4" /> AI Analysis</TabsTrigger>
              <TabsTrigger value="proposal" className="gap-2 data-[state=active]:bg-background data-[state=active]:text-purple-600 data-[state=active]:shadow-sm"><Sparkles className="w-4 h-4" /> Proposal Drafter</TabsTrigger>
            </TabsList>

            {/* Analysis Tab */}
            <TabsContent value="analyze" className="flex-1 flex flex-col gap-4 min-h-0 data-[state=active]:flex">
              <div className="flex-1 bg-card border border-border rounded-lg p-4 overflow-hidden flex flex-col shadow-sm">
                <ScrollArea className="flex-1 pr-4">
                  {analysis ? (
                    <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground">
                      <div className="whitespace-pre-wrap font-sans leading-relaxed">
                        {analysis}
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-muted-foreground opacity-80">
                      <Cpu className="w-12 h-12 mb-4 text-muted-foreground/50" />
                      <p>Ask Gemini to analyze documents, extract requirements, or find risks.</p>
                    </div>
                  )}
                </ScrollArea>
              </div>
              
              <div className="relative">
                <Textarea 
                  placeholder="Ask about requirements, deadlines, or compliance risks..." 
                  className="pr-12 bg-card border-border focus:border-primary focus:ring-primary/20 min-h-[80px] resize-none text-foreground placeholder:text-muted-foreground"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if(e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAnalyze();
                    }
                  }}
                />
                <Button 
                  size="icon" 
                  className="absolute right-3 bottom-3 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20"
                  onClick={handleAnalyze}
                  disabled={analyzing}
                >
                  {analyzing ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </TabsContent>

            {/* Proposal Tab */}
            <TabsContent value="proposal" className="flex-1 flex flex-col gap-4 min-h-0 data-[state=active]:flex">
               <div className="grid grid-cols-2 gap-4 h-full">
                 <div className="flex flex-col gap-2">
                   <label className="text-xs uppercase font-bold text-muted-foreground">Instructions</label>
                   <Textarea 
                     className="flex-1 bg-card border-border resize-none font-mono text-sm text-foreground focus:border-purple-500 focus:ring-purple-500/20"
                     placeholder="E.g., Write an executive summary focusing on our eco-friendly approach..."
                     value={instructions}
                     onChange={(e) => setInstructions(e.target.value)}
                   />
                   <Button 
                     className="w-full gap-2 bg-purple-600 hover:bg-purple-700 text-white shadow-md shadow-purple-500/20" 
                     onClick={handleGenerateProposal}
                     disabled={generating}
                   >
                     <Sparkles className="w-4 h-4" /> {generating ? 'Drafting...' : 'Generate Draft'}
                   </Button>
                 </div>
                 <div className="flex flex-col gap-2">
                   <label className="text-xs uppercase font-bold text-muted-foreground">Generated Draft</label>
                   <div className="flex-1 bg-card border border-border rounded-md p-4 overflow-y-auto shadow-sm">
                      <div className="prose prose-sm max-w-none whitespace-pre-wrap dark:prose-invert prose-headings:text-foreground prose-p:text-muted-foreground">
                        {proposal || <span className="text-muted-foreground italic">Draft will appear here...</span>}
                      </div>
                   </div>
                 </div>
               </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default TenderDetail;
